import {
  Button,
  ChakraSelect,
  FormControl,
  FormLabel,
  HStack,
  Stack,
  useBoolean,
  useDisclosure,
} from '@elkaso-app/web-design';
import { getCatalogItemUnitsEndpointIdentifier } from 'apis/restaurants/warehouse-items/use-get-catalog-item-units';
import { BaseFilterButton } from 'components/base-table/filters';
import { useGetQueryClientData } from 'hooks/use-get-query-client-data';
import { usePageParams } from 'hooks/use-page-params';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

type TOption = {
  value: string;
  label: string;
};

type TInputs = {
  unitId: TOption | undefined;
};

const defaultValues: TInputs = {
  unitId: undefined,
};

export const UnitIdFilterButton = () => {
  const [isActive, setIsActive] = useBoolean(false);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { getPageParams, setPageParams } = usePageParams();
  const params = getPageParams();

  const unitsData = useGetQueryClientData([getCatalogItemUnitsEndpointIdentifier, {}]);

  const unitOptions: TOption[] = useMemo(() => {
    return unitsData?.data?.map((item: any) => {
      return {
        value: item?.id,
        label: item?.abbreviation,
      };
    });
  }, [unitsData]);

  // Update active filter button
  useEffect(() => {
    if (params.unitId) setIsActive.on();
    else setIsActive.off();
  }, [params.unitId]);

  const { control, handleSubmit, formState, reset, setValue } = useForm<TInputs>({
    defaultValues: { ...defaultValues },
  });

  // Update values
  const updateValues = () => {
    setValue(
      'unitId',
      unitOptions.find((o) => {
        if (params?.unitId) {
          return o.value === params?.unitId;
        }

        return undefined;
      })
    );
  };

  useEffect(() => {
    updateValues();
  }, [params.unitId]);

  const onSubmit = (data: TInputs) => {
    params.unitId = data.unitId?.value as string;

    setPageParams(params);
    onClose();
    reset(data);
  };

  const handleClose = () => {
    onClose();
    updateValues();
  };

  const handleDisableFilterButton = () => {
    const isDisabled = Object.keys(formState.dirtyFields).length ? false : true;
    return isDisabled;
  };

  return (
    <BaseFilterButton
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={handleClose}
      isActive={isActive}
      formComponent={
        <Stack as='form' spacing='lg' p='md' pt='lg' onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name='unitId'
            render={({ field: { onChange, name, onBlur, value } }) => (
              <FormControl>
                <FormLabel>Unit</FormLabel>
                <ChakraSelect
                  id='unitId'
                  options={unitOptions}
                  onChange={onChange}
                  name={name}
                  onBlur={onBlur}
                  value={value}
                />
              </FormControl>
            )}
          />

          <HStack justify='flex-end'>
            <Button variant='outline' colorScheme='red' onClick={handleClose}>
              Cancel
            </Button>
            <Button variant='solid' colorScheme='red' type='submit' disabled={handleDisableFilterButton()}>
              Filter
            </Button>
          </HStack>
        </Stack>
      }
    />
  );
};
